import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  IconBillCheck,
  IconChart,
  IconEdit,
  IconIdCard,
  IconPill,
  IconPrinter,
  IconRefreshCw,
  IconStethoscope,
  IconWallet,
} from '../../components/layout/icons';
import { BillActionGrid, BillActionHoverTile, BillActionTile } from '../../components/ui/BillActionTile';
import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { openAuthenticatedRoute } from '../../lib/auth';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useInvestigationBillPatientBills } from '../investigation-bill/investigation-bill.queries';
import { EmgAllBillList } from './EmgAllBillList';
import { EmgAllReceiptList } from './EmgAllReceiptList';
import { EmgAllRefundList } from './EmgAllRefundList';
import { EmgEditBillForm } from './EmgEditBillForm';
import { EmgNewReceiptForm } from './EmgNewReceiptForm';
import { EmgPrescriptionForm } from './EmgPrescriptionForm';
import { useEmgPatientBills, useEmgRegistration } from './emg.queries';
import { currency, formatEmgDate } from './emg.utils';

type BillTab = 'create-prescription' | 'prescription' | 'bill-details' | 'edit-bill' | 'print-bill' | 'all-bill' | 'new-receipt' | 'all-receipt' | 'refund' | 'all-refund';

type BlankTab = { key: Exclude<BillTab, 'bill-details'>; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; soon: boolean; message: string };

const tabsBeforeBillDetails: BlankTab[] = [
  { key: 'create-prescription', label: 'Create E-Prescription', icon: IconPill, soon: false, message: 'Prescription creation is not available for EMG bills yet.' },
  { key: 'prescription', label: 'Prescription', icon: IconStethoscope, soon: true, message: 'Prescription records are not available for EMG bills yet.' },
];

const tabsAfterBillDetails: BlankTab[] = [
  { key: 'edit-bill', label: 'Edit Bill', icon: IconEdit, soon: false, message: 'Editing an EMG bill is not available yet.' },
  { key: 'print-bill', label: 'Print Bill', icon: IconPrinter, soon: false, message: 'This section is not available here yet. Use the Print button at the top of the page to print this bill.' },
  { key: 'all-bill', label: 'All Bill', icon: IconWallet, soon: false, message: 'A consolidated bill list is not available for EMG bills yet.' },
  { key: 'all-receipt', label: 'All Receipt', icon: IconChart, soon: false, message: 'A receipts list is not available for EMG bills yet.' },
  { key: 'all-refund', label: 'All Refund', icon: IconRefreshCw, soon: false, message: 'A refunds list is not available for EMG bills yet.' },
];

const blankTabs: BlankTab[] = [...tabsBeforeBillDetails, ...tabsAfterBillDetails];
const upper = (value: string | number | null | undefined) => String(value ?? '-').toUpperCase();

export const EmgRegistrationDetailPage = () => {
  const { registrationId } = useParams();
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState<BillTab>('bill-details');
  const query = useEmgRegistration(registrationId);
  const uhid = query.data?.patientSnapshot.uhid ?? null;
  const emgBillsQuery = useEmgPatientBills(uhid);
  const investigationBillsQuery = useInvestigationBillPatientBills(uhid);
  useEffect(() => { if (query.data && params.get('print') === '1') window.setTimeout(() => window.print(), 250); }, [params, query.data]);
  if (query.isLoading) return <DetailSkeleton label="Loading registration details" />;
  if (query.isError || !query.data) return <ErrorState title="EMG registration not found" message="The registration could not be loaded." />;
  const row = query.data;
  const address = [row.patientSnapshot.address, row.patientSnapshot.district, row.patientSnapshot.state, row.patientSnapshot.pinCode].filter(Boolean).join(', ') || '-';
  const billStatusLabel = row.billing.billStatus === 'due' ? 'Due' : row.billing.billStatus === 'cancelled' ? 'Cancelled' : 'Paid';
  const activeBlankTab = blankTabs.find((tab) => tab.key === activeTab);
  const activeEmgBills = (emgBillsQuery.data?.data ?? []).filter((bill) => bill.billing.billStatus !== 'cancelled');
  const activeInvestigationBills = (investigationBillsQuery.data?.data ?? []).filter((bill) => bill.billing.billStatus !== 'cancelled');
  const activeBills = [...activeEmgBills, ...activeInvestigationBills];
  const totalDue = activeBills.reduce((sum, bill) => sum + bill.billing.dueAmount, 0);
  const totalCredit = activeBills.reduce((sum, bill) => sum + bill.billing.creditAmount, 0);
  const printBill = () => openAuthenticatedRoute(`/emg/${row.id}/print-bill`);

  return (
    <div className="page-stack emg-page emg-bill-page">
      <div className="page-heading emg-print-hidden">
        <div><p className="eyebrow">EMG</p><h1>EMG Registration Details</h1></div>
        <div className="emg-heading-actions">
          <Link className="button button-secondary" to="/emg">Back</Link>
        </div>
      </div>

      <div className="emg-bill-layout">
        <aside className="content-card emg-bill-sidebar">
          <div className="emg-bill-patient-box">
            <p><strong>PATIENT NAME : </strong>{upper(row.patientSnapshot.name)}</p>
            <p><strong>UHID : </strong>{upper(row.patientSnapshot.uhid)}</p>
            <p><strong>SEX : </strong>{upper(row.patientSnapshot.gender)} || <strong>AGE : </strong>{row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</p>
            <p><strong>MOBILE : </strong>{upper(row.patientSnapshot.phone)}</p>
            <p><strong>ADDRESS : </strong>{upper(address)}</p>
            <p><strong>ATTENDING DOCTOR : </strong>{upper(doctorNameOrDash(row.attendingDoctor?.name))}</p>
          </div>
          {totalCredit > 0 || totalDue > 0 ? (
            <div className="opd-bill-patient-balance">
              {totalCredit > 0 ? <p><strong>Credit Amount : </strong><span className="opd-bill-patient-credit">{currency(totalCredit)}</span></p> : null}
              {totalDue > 0 ? <p><strong>Overall Due : </strong><span className="opd-bill-patient-due">{currency(totalDue)}</span></p> : null}
            </div>
          ) : null}
          <BillActionGrid classPrefix="emg-bill" printHiddenClass="emg-print-hidden">
            <BillActionTile classPrefix="emg-bill" icon={IconIdCard} label="Patient Profile" to={`/patients/${row.patient.id}`} />
            {tabsBeforeBillDetails.map((tab) => (
              tab.key === 'prescription' ? (
                <BillActionHoverTile
                  key={tab.key}
                  classPrefix="emg-bill"
                  icon={tab.icon}
                  label={tab.label}
                  items={[
                    { label: 'Prescription without letterhead', onClick: () => openAuthenticatedRoute(`/emg/${row.id}/print-prescription?header=0`) },
                    { label: 'Prescription with letterhead', onClick: () => openAuthenticatedRoute(`/emg/${row.id}/print-prescription?header=1`) },
                  ]}
                />
              ) : tab.key === 'print-bill' ? (
                <BillActionTile key={tab.key} classPrefix="emg-bill" icon={tab.icon} label={tab.label} onClick={printBill} />
              ) : (
                <BillActionTile key={tab.key} classPrefix="emg-bill" icon={tab.icon} label={tab.label} active={activeTab === tab.key} soon={tab.soon} onClick={() => setActiveTab(tab.key)} />
              )
            ))}
            <BillActionTile classPrefix="emg-bill" icon={IconBillCheck} label="Bill Details" active={activeTab === 'bill-details'} onClick={() => setActiveTab('bill-details')} />
            {row.billing.dueAmount > 0 ? <BillActionTile classPrefix="emg-bill" icon={IconWallet} label="New Receipt" active={activeTab === 'new-receipt'} onClick={() => setActiveTab('new-receipt')} /> : null}
            {row.billing.creditAmount > 0 ? <BillActionTile classPrefix="emg-bill" icon={IconRefreshCw} label="Refund" active={activeTab === 'refund'} onClick={() => setActiveTab('refund')} /> : null}
            {tabsAfterBillDetails.map((tab) => (
              tab.key === 'print-bill' ? (
                <BillActionTile key={tab.key} classPrefix="emg-bill" icon={tab.icon} label={tab.label} onClick={printBill} />
              ) : (
                <BillActionTile key={tab.key} classPrefix="emg-bill" icon={tab.icon} label={tab.label} active={activeTab === tab.key} soon={tab.soon} onClick={() => setActiveTab(tab.key)} />
              )
            ))}
          </BillActionGrid>
        </aside>

        <section className="content-card emg-bill">
          {activeTab === 'create-prescription' ? (
            <EmgPrescriptionForm row={row} />
          ) : activeTab === 'edit-bill' ? (
            <EmgEditBillForm row={row} onCancelled={() => setActiveTab('bill-details')} />
          ) : activeTab === 'new-receipt' ? (
            <EmgNewReceiptForm row={row} onRecorded={() => setActiveTab('bill-details')} />
          ) : activeTab === 'all-bill' ? (
            <EmgAllBillList row={row} />
          ) : activeTab === 'all-receipt' ? (
            <EmgAllReceiptList row={row} />
          ) : activeTab === 'refund' || activeTab === 'all-refund' ? (
            <EmgAllRefundList row={row} showForm={activeTab === 'refund'} />
          ) : activeBlankTab ? (
            <EmptyState
              title={`${activeBlankTab.label}${activeBlankTab.soon ? ' — Coming soon' : ''}`}
              message={activeBlankTab.message}
              action={{ label: 'Back to Bill Details', onClick: () => setActiveTab('bill-details') }}
            />
          ) : (
          <>
          <header className="emg-bill-banner">
            <div className="emg-bill-banner-row">
              <div className="emg-bill-banner-fields">
                <span>Bill No : {row.billing.billNumber}</span>
                <span>Bill Date : {formatEmgDate(row.registrationDate)}</span>
                <span>Bill Section : EMG</span>
              </div>
              <span className={`emg-badge ${row.billing.billStatus}`}>{billStatusLabel}</span>
            </div>
            <p>Bill Created By - {row.generatedBy.name}{row.editedBy ? ` || Last Edit By - ${row.editedBy.name}` : ''}</p>
          </header>

          <div className="table-scroll">
            <table className="emg-bill-charge-table">
              <thead><tr><th>#</th><th>Date</th><th>Charge Name</th><th>Charge Amount</th><th>Qty</th><th>Dis Amt</th><th>Dis %</th><th>Status</th><th>Total</th></tr></thead>
              <tbody>
                {row.charges.length === 0
                  ? <tr><td className="emg-bill-empty" colSpan={9}>No itemized charge rows were stored for this legacy bill.</td></tr>
                  : row.charges.map((line, index) => (
                    <tr key={line.charge} className={row.billing.billStatus === 'paid' ? 'paid' : undefined}>
                      <td>{index + 1}</td>
                      <td>{formatEmgDate(row.registrationDate)}</td>
                      <td>{line.chargeName}</td>
                      <td>{currency(line.rate)}</td>
                      <td>{line.quantity}</td>
                      <td>{currency(line.discountAmount)}</td>
                      <td>{line.discountPercent}%</td>
                      <td><span className={`emg-badge ${row.billing.billStatus}`}>{billStatusLabel}</span></td>
                      <td>{currency(line.amount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="emg-bill-summary">
            <div><span>Total</span><strong>{currency(row.billing.total)}</strong></div>
            <div><span>Discount</span><strong>{currency(row.billing.totalDiscount)}</strong></div>
            <div><span>Payable Amount</span><strong>{currency(row.billing.grandTotal)}</strong></div>
            <div><span>Total Payment</span><strong>{currency(row.billing.totalPayment)}</strong></div>
            {row.billing.totalRefunded > 0 ? <div className="refund"><span>Total Refund</span><strong>{currency(row.billing.totalRefunded)}</strong></div> : null}
            {row.billing.dueAmount > 0 ? <div className="due"><span>Due</span><strong>{currency(row.billing.dueAmount)}</strong></div> : null}
            {row.billing.creditAmount > 0 ? <div className="credit"><span>Credit</span><strong>{currency(row.billing.creditAmount)}</strong></div> : null}
          </div>

          <section className="emg-bill-subsection">
            <div className="emg-bill-subsection-head">
              <h3>Payment / Receipt List</h3>
              <span>Total Payment <strong>{currency(row.billing.totalPayment)}</strong></span>
            </div>
            {row.payments.length === 0 ? (
              <p className="emg-bill-empty-note">No payments recorded for this bill.</p>
            ) : (
              <div className="table-scroll">
                <table className="emg-bill-payment-table">
                  <thead><tr><th>#</th><th>Date</th><th>Mode</th><th>Bank</th><th>Amount</th></tr></thead>
                  <tbody>
                    {row.payments.map((payment, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{formatEmgDate(row.registrationDate)}</td>
                        <td>{payment.mode}</td>
                        <td>{payment.bankName ?? '-'}</td>
                        <td>{currency(payment.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="emg-bill-subsection">
            <h3>Referral Details</h3>
            <dl className="emg-bill-referral">
              <div><dt>Market By</dt><dd>{row.marketBy?.name ?? '-'}</dd></div>
              <div><dt>Provider</dt><dd>{row.provider?.name ?? '-'}</dd></div>
              <div><dt>Referred By</dt><dd>{row.referredBy?.name ?? '-'}</dd></div>
            </dl>
          </section>
          </>
          )}
        </section>
      </div>
    </div>
  );
};
